import type { Devis, FinancementOverrides } from "@shared/schema";

export const FINANCEMENT_COEFFICIENTS: Record<string, number> = {
  "12": 9.01,
  "24": 4.78,
  "36": 3.34,
  "48": 2.61,
  "60": 2.10,
  "72": 2.00,
};

export const FINANCEMENT_DUREES = ["12", "24", "36", "48", "60", "72"] as const;
export type DureeFinancement = (typeof FINANCEMENT_DUREES)[number];

export function hasFinancementCoefficient(duree: string): boolean {
  const coefficient = FINANCEMENT_COEFFICIENTS[duree];
  return typeof coefficient === "number" && Number.isFinite(coefficient);
}

export function calcLoyerAuto(baseCapitalFinance: number, duree: string): number {
  const coeff = FINANCEMENT_COEFFICIENTS[duree] ?? FINANCEMENT_COEFFICIENTS["36"];
  return baseCapitalFinance > 0 ? baseCapitalFinance * coeff / 100 : 0;
}

type FinancementDevis = Pick<
  Devis,
  | "dureesFinancement"
  | "dureeFinancementDevis"
  | "optionFinancement"
  | "financementOverrides"
  | "loyerRobotAccessoiresEdite"
  | "maintenanceMensuelleEditee"
>;

function getOverrides(devis: FinancementDevis): FinancementOverrides {
  const overrides = devis.financementOverrides;
  return overrides && typeof overrides === "object" && !Array.isArray(overrides)
    ? overrides
    : {};
}

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function resolveDurees(devis: FinancementDevis): string[] {
  if (Array.isArray(devis.dureesFinancement) && devis.dureesFinancement.length > 0) {
    return devis.dureesFinancement;
  }

  const legacyDuration = devis.dureeFinancementDevis?.trim();
  if (legacyDuration) return [legacyDuration];

  return devis.optionFinancement ? ["36"] : [];
}

export function resolveLoyer(
  devis: FinancementDevis,
  duree: string,
  loyerAuto: number | null,
): number | null {
  const durees = resolveDurees(devis);
  const overrides = getOverrides(devis);

  if (durees.includes(duree)) {
    const override = overrides[duree]?.loyer;
    if (typeof override === "number" && Number.isFinite(override)) {
      return override;
    }
  }

  // Legacy scalar override is valid only for the single effective column.
  if (Object.keys(overrides).length === 0 && durees.length === 1 && durees[0] === duree) {
    const legacyOverride = numericValue(devis.loyerRobotAccessoiresEdite);
    if (legacyOverride !== null) return legacyOverride;
  }

  return loyerAuto;
}

export function resolveMaintenance(
  devis: FinancementDevis,
  duree: string,
  maintenanceAuto: number,
): number {
  const durees = resolveDurees(devis);
  const overrides = getOverrides(devis);

  if (durees.includes(duree)) {
    const override = overrides[duree]?.maintenance;
    if (typeof override === "number" && Number.isFinite(override)) {
      return override;
    }
  }

  // Legacy scalar override is valid only for the single effective column.
  if (Object.keys(overrides).length === 0 && durees.length === 1 && durees[0] === duree) {
    const legacyOverride = numericValue(devis.maintenanceMensuelleEditee);
    if (legacyOverride !== null) return legacyOverride;
  }

  return maintenanceAuto;
}
